import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { BadgeCheck, Coins, ExternalLink, Shuffle, Truck } from "lucide-react";
import { PointsEarn, PointsNeeded } from "@/components/club/PointsEarn";
import { WatchButton } from "./WatchButton";
import { HouseOffersBox } from "./HouseOffersBox";
import { useMe } from "@/context/MeContext";
import { eur } from "@/lib/api";
import { FateloVoiDialog } from "./FateloVoiDialog";
import { DeclareDialog } from "./DeclareDialog";

function OfferRow({ offer, index, best, onAction }) {
  return (
    <div
      data-testid={`offer-row-${index}`}
      className={`fade-up grid gap-4 rounded-2xl border p-5 transition-colors duration-200 sm:grid-cols-[96px_1.4fr_1fr_auto] sm:items-center ${best ? "border-amber-500/50 bg-amber-500/[0.06]" : "border-white/8 bg-[#12141C] hover:bg-[#161924]"}`}
      style={{ animationDelay: `${index * 70}ms` }}
    >
      <div className="grid h-24 w-24 place-items-center overflow-hidden rounded-xl bg-white" data-testid={`offer-image-${index}`}>
        {offer.image_url ? (
          <img src={offer.image_url} alt={offer.product_title || offer.shop} referrerPolicy="no-referrer" className="h-full w-full object-contain p-1.5"
            onError={(e) => { e.currentTarget.replaceWith(Object.assign(document.createElement("span"), { className: "px-2 text-center text-[10px] text-slate-500", textContent: "Immagine non disponibile" })); }} />
        ) : <span className="px-2 text-center text-[10px] text-slate-500">Immagine non disponibile</span>}
      </div>
      <div>
        <div className="flex items-center gap-2">
          <p className="font-display text-lg font-semibold text-white">{offer.shop}</p>
          {best && <span data-testid="best-offer-badge" className="inline-flex items-center gap-1 rounded-full bg-amber-500 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-black"><BadgeCheck className="h-3 w-3" /> Offerta migliore</span>}
        </div>
        {offer.match === "simile" ? (
          <p data-testid={`offer-similar-${index}`} className="mt-1 inline-flex items-start gap-1 rounded-lg bg-sky-500/10 px-2 py-1 text-xs text-sky-300"><Shuffle className="mt-0.5 h-3 w-3 shrink-0" /> Prodotto simile{offer.match_note ? `: ${offer.match_note}` : ""}</p>
        ) : (
          <p data-testid={`offer-identical-${index}`} className="mt-1 text-xs text-emerald-400">Stessa marca e modello</p>
        )}
        {offer.product_title && <p className="mt-1 line-clamp-1 text-xs text-slate-500">{offer.product_title}</p>}
        <p className="mt-1 text-sm text-slate-400">{offer.availability || "Disponibilità da verificare"}</p>
        <a href={offer.affiliate_url || offer.url} target="_blank" rel="noreferrer" data-testid={`offer-source-${index}`} className="mt-1 inline-flex items-center gap-1 text-xs text-slate-500 hover:text-slate-300">
          Vedi fonte <ExternalLink className="h-3 w-3" />
        </a>
      </div>
      <div>
        <p data-testid={`offer-price-${index}`} className="font-display text-2xl font-bold text-white">{eur(offer.price)}</p>
        <p className="flex items-center gap-1 text-xs text-slate-400"><Truck className="h-3.5 w-3.5" /> {offer.shipping > 0 ? `+ ${eur(offer.shipping)} spedizione` : "Spedizione gratuita"}</p>
        <p className="text-xs text-slate-500">Totale {eur(offer.total)}</p>
        {offer.affiliated && <PointsEarn amount={offer.price} multiplier={1} label="Compro da solo" testid={`offer-points-self-${index}`} />}
        <PointsEarn amount={offer.price} multiplier={2} label="Fatelo voi" testid={`offer-points-fatelo-${index}`} />
        <PointsNeeded amount={offer.total} note="spedizione inclusa" testid={`offer-points-needed-${index}`} />
      </div>
      <div className="flex flex-wrap gap-2 sm:flex-col">
        {offer.affiliated && <button data-testid={`buy-self-btn-${index}`} onClick={() => onAction("self", index)} className="rounded-full border border-white/15 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-white/10">Compro da solo</button>}
        <button data-testid={`fatelo-voi-btn-${index}`} onClick={() => onAction("fatelo", index)} className="rounded-full bg-amber-500 px-4 py-2 text-sm font-semibold text-black transition-colors hover:bg-amber-400">Fatelo voi · 2 pt/€</button>
        <button data-testid={`pay-with-points-button-${index}`} onClick={() => onAction("points", index)} className="inline-flex items-center justify-center gap-1 rounded-full border border-amber-500/40 px-4 py-2 text-sm font-medium text-amber-300 transition-colors hover:bg-amber-500/10">
          <Coins className="h-4 w-4" /> Paga con i punti
        </button>
      </div>
    </div>
  );
}

export function SearchResults({ result }) {
  const { isSignedIn } = useMe();
  const navigate = useNavigate();
  const [dialog, setDialog] = useState(null);
  const p = result.product || {};

  const onAction = (kind, index) => {
    if (!isSignedIn) return navigate("/sign-in");
    if (kind === "self") window.open(result.offers[index].affiliate_url, "_blank", "noopener");
    setDialog({ kind, index });
  };

  return (
    <section className="mx-auto max-w-7xl px-4 pb-20 sm:px-6" data-testid="search-results">
      <div className="grid gap-8 lg:grid-cols-[320px_1fr]">
        <aside className="h-fit rounded-3xl border border-white/10 bg-[#12141C] p-6 lg:sticky lg:top-24">
          {p.image_url && <img src={p.image_url} alt={p.name} className="mb-5 aspect-square w-full rounded-2xl bg-white object-contain p-4" onError={(e) => (e.currentTarget.style.display = "none")} />}
          <p className="text-xs uppercase tracking-widest text-slate-500">{p.brand}</p>
          <h2 data-testid="product-name" className="mt-1 font-display text-lg font-semibold text-white md:text-lg">{p.name}</h2>
          {p.original_price > 0 && <p className="mt-3 text-sm text-slate-400">Prezzo sul link: <span className="text-white line-through decoration-red-400/70">{eur(p.original_price)}</span> {p.original_shop && `(${p.original_shop})`}</p>}
          {result.saving > 0 && (
            <div className="mt-4 rounded-2xl bg-emerald-500/10 p-4">
              <p className="text-xs uppercase tracking-widest text-emerald-300">Risparmi</p>
              <p data-testid="saving-amount" className="font-display text-3xl font-bold text-emerald-400">{eur(result.saving)}</p>
            </div>
          )}
          <WatchButton result={result} />
        </aside>
        <div className="space-y-3">
          <HouseOffersBox offers={result.house_offers} />
          <p className="text-sm text-slate-400">{result.offers.length} offerte trovate: prima i prodotti identici, poi quelli simili, dal più conveniente</p>
          {result.offers.map((o, i) => <OfferRow key={i} offer={o} index={i} best={i === 0} onAction={onAction} />)}
        </div>
      </div>
      {dialog && dialog.kind !== "self" && (
        <FateloVoiDialog result={result} index={dialog.index} usePoints={dialog.kind === "points"} onClose={() => setDialog(null)} />
      )}
      {dialog?.kind === "self" && <DeclareDialog result={result} index={dialog.index} onClose={() => setDialog(null)} />}
    </section>
  );
}
